/**
 * Template data for Groundhog Day booking generation
 * Contains comprehensive guest profiles with varied data to test all booking features
 */

import {
  AdminNote,
  Comment,
  DietaryRequirement,
  GroupMember,
  Item
} from "../../types/bookings";

export type TemplateGuest = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  numPeople: number;
  preferredTimeSlot: "lunch" | "afternoon" | "dinner";
  preferredTime: string;
  duration: number;
  category:
    | "regular"
    | "business"
    | "family"
    | "large_party"
    | "vip"
    | "walkin"
    | "waitlisted";
  dietaryRequirements?: DietaryRequirement[];
  notes?: string;
  adminNotes?: Omit<AdminNote, "time">[];
  comments?: Omit<Comment, "time">[];
  groupMembers?: Omit<GroupMember, "id">[];
  labels?: string[];
  customerLabels?: string[];
  tags?: { id: string; label: string; value: string; type: string }[];
  hasDeposit?: boolean;
  depositAmount?: number;
  hasPreorder?: boolean;
  preorderItems?: Omit<Item, "id">[];
  minSpend?: { amount: number; perHead: boolean };
};

// Time slot ranges
export const TIME_SLOTS = {
  lunch: { start: "12:00", end: "14:30" },
  afternoon: { start: "15:00", end: "17:30" },
  dinner: { start: "18:00", end: "22:00" }
};

// Booking status distribution weights
export const STATUS_WEIGHTS = {
  confirmed: 0.4,
  in_progress: 0.25,
  new: 0.15,
  complete: 0.1,
  cancelled: 0.05,
  waitlisted: 0.05
};

// Stage progression
export const BOOKING_STAGES = [
  "enquiry",
  "confirmed",
  "arrived",
  "seated",
  "dessert",
  "left"
];

// Common dietary requirements
export const DIETARY_OPTIONS = {
  vegetarian: { diet_types: ["vegetarian"], allergies: [] },
  vegan: { diet_types: ["vegan"], allergies: [] },
  glutenFree: { diet_types: ["gluten-free"], allergies: ["gluten"] },
  nutAllergy: { diet_types: [], allergies: ["nuts"] },
  dairyFree: { diet_types: ["dairy-free"], allergies: ["dairy"] },
  halal: { diet_types: ["halal"], allergies: [] },
  kosher: { diet_types: ["kosher"], allergies: [] },
  shellfish: { diet_types: [], allergies: ["shellfish"] }
};

// Sample preorder items
export const SAMPLE_PREORDER_ITEMS: Omit<Item, "id">[] = [
  {
    name: "Sharing Platter",
    quantity: 1,
    total: 2500,
    price: 2500,
    base_price: 2500,
    options_price: 0,
    discount_value: 0,
    type: "food",
    sub_type: "starter",
    plu: 1001,
    for_name: "",
    items: []
  },
  {
    name: "House Red Wine (Bottle)",
    quantity: 2,
    total: 4800,
    price: 2400,
    base_price: 2400,
    options_price: 0,
    discount_value: 0,
    type: "drink",
    sub_type: "wine",
    plu: 2001,
    for_name: "",
    items: []
  },
  {
    name: "Champagne (Bottle)",
    quantity: 1,
    total: 6500,
    price: 6500,
    base_price: 6500,
    options_price: 0,
    discount_value: 0,
    type: "drink",
    sub_type: "champagne",
    plu: 2010,
    for_name: "",
    items: []
  },
  {
    name: "Birthday Cake",
    quantity: 1,
    total: 3500,
    price: 3500,
    base_price: 3500,
    options_price: 0,
    discount_value: 0,
    type: "food",
    sub_type: "dessert",
    plu: 3001,
    for_name: "",
    items: []
  },
  {
    name: "Set Menu - 3 Course",
    quantity: 4,
    total: 14000,
    price: 3500,
    base_price: 3500,
    options_price: 0,
    discount_value: 0,
    type: "food",
    sub_type: "set_menu",
    plu: 4001,
    for_name: "",
    items: []
  }
];

/**
 * Comprehensive template guests covering all booking scenarios
 */
export const TEMPLATE_GUESTS: TemplateGuest[] = [
  // Regular Diners (3 guests)
  {
    firstName: "Emma",
    lastName: "Thompson",
    email: "emma.thompson@email.com",
    phone: "07700900001",
    numPeople: 2,
    preferredTimeSlot: "dinner",
    preferredTime: "19:00",
    duration: 90,
    category: "regular",
    notes: "Window seat preferred"
  },
  {
    firstName: "James",
    lastName: "Wilson",
    email: "james.wilson@email.com",
    phone: "07700900002",
    numPeople: 4,
    preferredTimeSlot: "dinner",
    preferredTime: "20:00",
    duration: 120,
    category: "regular",
    dietaryRequirements: [
      {
        name: "James",
        diet_types: ["vegetarian"],
        allergies: [],
        notes: "Pescatarian also fine"
      }
    ]
  },
  {
    firstName: "Sophie",
    lastName: "Brown",
    email: "sophie.brown@email.com",
    phone: "07700900003",
    numPeople: 3,
    preferredTimeSlot: "lunch",
    preferredTime: "12:30",
    duration: 75,
    category: "regular",
    comments: [
      {
        comment: "Regular customer - always tips well",
        full_name: "Staff Member"
      }
    ]
  },

  // Business Diners (3 guests)
  {
    firstName: "Richard",
    lastName: "Sterling",
    email: "r.sterling@acmecorp.com",
    phone: "07700900004",
    company: "Acme Corporation",
    numPeople: 4,
    preferredTimeSlot: "lunch",
    preferredTime: "13:00",
    duration: 90,
    category: "business",
    notes: "Client lunch - needs quiet area",
    hasDeposit: true,
    depositAmount: 5000,
    minSpend: { amount: 15000, perHead: false },
    labels: ["packages_requested"],
    tags: [
      { id: "occasion", label: "Occasion", value: "Business", type: "text" }
    ]
  },
  {
    firstName: "Victoria",
    lastName: "Chen",
    email: "v.chen@globaltech.io",
    phone: "07700900005",
    company: "GlobalTech Solutions",
    numPeople: 6,
    preferredTimeSlot: "dinner",
    preferredTime: "19:30",
    duration: 150,
    category: "business",
    notes: "Important client dinner - CEO attending",
    hasDeposit: true,
    depositAmount: 10000,
    hasPreorder: true,
    preorderItems: [SAMPLE_PREORDER_ITEMS[1], SAMPLE_PREORDER_ITEMS[2]],
    adminNotes: [
      {
        note: "VIP treatment required - CEO of major client",
        full_name: "Manager"
      }
    ],
    minSpend: { amount: 5000, perHead: true }
  },
  {
    firstName: "Michael",
    lastName: "Roberts",
    email: "michael.roberts@lawfirm.co.uk",
    phone: "07700900006",
    company: "Roberts & Partners LLP",
    numPeople: 2,
    preferredTimeSlot: "lunch",
    preferredTime: "12:00",
    duration: 60,
    category: "business",
    notes: "Quick business lunch",
    customerLabels: ["corporate"]
  },

  // Family Groups (2 guests)
  {
    firstName: "Sarah",
    lastName: "Mitchell",
    email: "sarah.mitchell@family.com",
    phone: "07700900007",
    numPeople: 5,
    preferredTimeSlot: "dinner",
    preferredTime: "18:00",
    duration: 120,
    category: "family",
    notes: "2 children (ages 5 and 8) - need high chairs",
    dietaryRequirements: [
      {
        name: "Tom (child)",
        diet_types: [],
        allergies: ["nuts"],
        notes: "Severe nut allergy - requires separate preparation"
      },
      {
        name: "Sarah",
        diet_types: ["gluten-free"],
        allergies: ["gluten"],
        notes: "Coeliac disease"
      }
    ],
    tags: [
      {
        id: "occasion",
        label: "Occasion",
        value: "Family Dinner",
        type: "text"
      }
    ]
  },
  {
    firstName: "David",
    lastName: "Taylor",
    email: "david.taylor@email.com",
    phone: "07700900008",
    numPeople: 8,
    preferredTimeSlot: "lunch",
    preferredTime: "13:00",
    duration: 150,
    category: "family",
    notes: "Birthday celebration for grandmother - 80th birthday",
    hasPreorder: true,
    preorderItems: [SAMPLE_PREORDER_ITEMS[3]],
    dietaryRequirements: [
      {
        name: "Grandma",
        diet_types: [],
        allergies: ["shellfish"],
        notes: "No shellfish"
      },
      {
        name: "Uncle Bob",
        diet_types: ["vegetarian"],
        allergies: []
      }
    ],
    tags: [
      { id: "occasion", label: "Occasion", value: "Birthday", type: "text" }
    ],
    labels: ["packages_requested"]
  },

  // Large Parties (2 guests)
  {
    firstName: "Jennifer",
    lastName: "Adams",
    email: "jennifer.adams@events.com",
    phone: "07700900009",
    numPeople: 12,
    preferredTimeSlot: "dinner",
    preferredTime: "19:00",
    duration: 180,
    category: "large_party",
    notes: "Hen party - need private area if possible",
    hasDeposit: true,
    depositAmount: 20000,
    hasPreorder: true,
    preorderItems: [
      SAMPLE_PREORDER_ITEMS[0],
      SAMPLE_PREORDER_ITEMS[1],
      SAMPLE_PREORDER_ITEMS[2]
    ],
    groupMembers: [
      { first_name: "Lisa", last_name: "Smith", email: "lisa@email.com" },
      { first_name: "Amy", last_name: "Jones", email: "amy@email.com" },
      { first_name: "Rachel", last_name: "Green", email: "rachel@email.com" }
    ],
    dietaryRequirements: [
      { name: "Lisa", diet_types: ["vegan"], allergies: [] },
      { name: "Amy", diet_types: [], allergies: ["dairy"] }
    ],
    adminNotes: [
      { note: "Confirmed deposit received", full_name: "Reservations" },
      { note: "Decorations allowed - balloons only", full_name: "Manager" }
    ],
    tags: [
      { id: "occasion", label: "Occasion", value: "Hen Party", type: "text" }
    ]
  },
  {
    firstName: "Robert",
    lastName: "Johnson",
    email: "robert.johnson@company.com",
    phone: "07700900010",
    company: "Johnson Enterprises",
    numPeople: 20,
    preferredTimeSlot: "dinner",
    preferredTime: "18:30",
    duration: 240,
    category: "large_party",
    notes: "Company Christmas party - private hire of back room",
    hasDeposit: true,
    depositAmount: 50000,
    hasPreorder: true,
    preorderItems: [SAMPLE_PREORDER_ITEMS[4]],
    minSpend: { amount: 100000, perHead: false },
    groupMembers: [
      {
        first_name: "HR",
        last_name: "Contact",
        email: "hr@johnson.com",
        phone: "07700900099"
      }
    ],
    dietaryRequirements: [
      { name: "3 guests", diet_types: ["vegetarian"], allergies: [] },
      { name: "1 guest", diet_types: ["vegan"], allergies: [] },
      { name: "2 guests", diet_types: [], allergies: ["gluten"] }
    ],
    labels: ["packages_requested"],
    customerLabels: ["corporate"],
    tags: [
      {
        id: "occasion",
        label: "Occasion",
        value: "Christmas Party",
        type: "text"
      }
    ]
  },

  // VIP/Repeat Customers (2 guests)
  {
    firstName: "Alexander",
    lastName: "Wright",
    email: "alex.wright@vip.com",
    phone: "07700900011",
    numPeople: 2,
    preferredTimeSlot: "dinner",
    preferredTime: "20:30",
    duration: 120,
    category: "vip",
    notes: "VIP - always gets best table. Prefers booth 1.",
    customerLabels: ["vip", "regular"],
    comments: [
      { comment: "Visited 50+ times this year", full_name: "Manager" },
      { comment: "Prefers Dom Perignon", full_name: "Sommelier" }
    ],
    adminNotes: [{ note: "Comp dessert on every visit", full_name: "Owner" }]
  },
  {
    firstName: "Charlotte",
    lastName: "Davies",
    email: "charlotte.davies@email.com",
    phone: "07700900012",
    numPeople: 4,
    preferredTimeSlot: "dinner",
    preferredTime: "19:00",
    duration: 150,
    category: "vip",
    notes: "Anniversary dinner - same table as first date please (Table 7)",
    customerLabels: ["vip"],
    hasPreorder: true,
    preorderItems: [SAMPLE_PREORDER_ITEMS[2]],
    tags: [
      { id: "occasion", label: "Occasion", value: "Anniversary", type: "text" }
    ],
    comments: [
      { comment: "10th wedding anniversary", full_name: "Reservations" }
    ]
  },

  // Walk-ins (2 guests)
  {
    firstName: "Oliver",
    lastName: "Martin",
    email: "oliver.martin@email.com",
    phone: "07700900013",
    numPeople: 2,
    preferredTimeSlot: "lunch",
    preferredTime: "14:00",
    duration: 60,
    category: "walkin"
  },
  {
    firstName: "Emily",
    lastName: "Clark",
    email: "emily.clark@email.com",
    phone: "07700900014",
    numPeople: 3,
    preferredTimeSlot: "afternoon",
    preferredTime: "16:00",
    duration: 75,
    category: "walkin",
    dietaryRequirements: [
      { name: "Emily", diet_types: ["vegetarian"], allergies: [] }
    ]
  },

  // Waitlisted (1 guest)
  {
    firstName: "Thomas",
    lastName: "Anderson",
    email: "thomas.anderson@email.com",
    phone: "07700900015",
    numPeople: 4,
    preferredTimeSlot: "dinner",
    preferredTime: "20:00",
    duration: 120,
    category: "waitlisted",
    notes: "Flexible on time - just wants Saturday dinner"
  }
];

/**
 * Get a random status based on weights
 */
export const getRandomStatus = (): string => {
  const random = Math.random();
  let cumulative = 0;

  for (const [status, weight] of Object.entries(STATUS_WEIGHTS)) {
    cumulative += weight;
    if (random <= cumulative) {
      return status === "confirmed" ? "complete" : status;
    }
  }

  return "in_progress";
};

/**
 * Get a random stage based on status
 */
export const getRandomStage = (
  status: string,
  availableStages: string[]
): string | undefined => {
  if (status === "new" || status === "cancelled") {
    return undefined;
  }

  if (status === "complete") {
    return availableStages.find(
      (s) =>
        s.toLowerCase().includes("left") || s.toLowerCase().includes("complete")
    );
  }

  // For in_progress, pick a random stage from the progression
  const progressionStages = availableStages.filter(
    (s) =>
      !s.toLowerCase().includes("left") && !s.toLowerCase().includes("complete")
  );

  if (progressionStages.length === 0) {
    return availableStages[0];
  }

  return progressionStages[
    Math.floor(Math.random() * progressionStages.length)
  ];
};

/**
 * Generate a time with slight variation
 */
export const getTimeWithVariation = (
  baseTime: string,
  variationMinutes: number = 15
): string => {
  const [hours, minutes] = baseTime.split(":").map(Number);
  const totalMinutes = hours * 60 + minutes;
  const variation =
    Math.floor(Math.random() * (variationMinutes * 2 + 1)) - variationMinutes;
  const newTotalMinutes = Math.max(
    0,
    Math.min(23 * 60 + 45, totalMinutes + variation)
  );
  const newHours = Math.floor(newTotalMinutes / 60);
  const newMinutes = Math.round((newTotalMinutes % 60) / 15) * 15;

  return `${String(newHours).padStart(2, "0")}:${String(newMinutes % 60).padStart(2, "0")}`;
};

/**
 * Generate a unique ID
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};
